// src/components/Investment/InvestmentCreation.js - FIXED TO USE WALLET ENDPOINT + CONSTANTS
import React, { useState, useEffect } from 'react';
import { investmentService } from '../../services/investmentService';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../utils/constants';
import './InvestmentCreation.css';

const InvestmentCreation = ({ stock, questionnaire, onSuccess, onClose }) => {
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const { currency, formatCurrency } = useCurrency();

  useEffect(() => {
    fetchWalletBalance();
    fetchExchangeRate();
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/wallet/balance/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('💰 Wallet Balance Response:', data);
        console.log('💱 Currency in response:', data.currency);
        console.log('📊 Balance value:', data.balance);
        
        setPortfolioSummary({
          wallet_balance: data.balance,
          available_balance: data.available_balance,
          currency: data.currency
        });
      } else {
        console.error('Failed to fetch wallet balance');
        setPortfolioSummary({ wallet_balance: 0, available_balance: 0 });
      }
    } catch (err) {
      console.error('Error fetching wallet balance:', err);
      setPortfolioSummary({ wallet_balance: 0, available_balance: 0 });
    }
  };

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/currency/rate/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        console.log('💱 Exchange Rate in Creation:', data);
        setExchangeRate(data);
      }
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
    }
  };

  const convertFromNGN = (ngnAmount) => {
    if (!exchangeRate) {
      console.warn('⚠️ No exchange rate available for NGN to USD conversion');
      return ngnAmount;
    }
    
    const amount = parseFloat(ngnAmount);
    const rate = parseFloat(exchangeRate.ngn_to_usd);
    const converted = amount * rate;
    
    console.log('💱 NGN→USD:', {
      ngnAmount: amount,
      rate: rate,
      usdAmount: converted
    });
    
    return converted;
  };

  const getInvestmentAmountDisplay = () => {
    if (!questionnaire.intended_investment_amount) return formatCurrency(0);
    
    const amountInNGN = parseFloat(questionnaire.intended_investment_amount);
    
    console.log('💰 Investment Amount:', {
      storedNGN: amountInNGN,
      currency: currency
    });
    
    if (currency === 'USD' && exchangeRate) {
      const amountInUSD = convertFromNGN(amountInNGN);
      console.log('✅ Displaying in USD:', amountInUSD);
      return formatCurrency(amountInUSD);
    }
    
    return formatCurrency(amountInNGN);
  };

  const getStockPriceDisplay = () => {
    if (stock.current_price_display) {
      return stock.current_price_display;
    }
    
    if (stock.formatted_price) {
      return stock.formatted_price;
    }
    
    const priceInNGN = parseFloat(stock.current_price);
    
    if (currency === 'USD' && exchangeRate) {
      const priceInUSD = convertFromNGN(priceInNGN);
      return formatCurrency(priceInUSD);
    }
    
    return formatCurrency(priceInNGN);
  };

  const calculateQuantity = () => {
    if (!questionnaire.intended_investment_amount) return 0;
    
    const amountInNGN = parseFloat(questionnaire.intended_investment_amount);
    const priceInNGN = parseFloat(stock.current_price);
    const shares = amountInNGN / priceInNGN;
    
    console.log('📊 Shares:', {
      amountNGN: amountInNGN,
      priceNGN: priceInNGN,
      shares: shares
    });
    
    return shares;
  };

  const getWalletBalanceDisplay = () => {
    if (!portfolioSummary) return formatCurrency(0);
    
    const balance = portfolioSummary.wallet_balance || 0;
    
    console.log('💰 Wallet balance (already converted by backend):', balance);
    console.log('💱 Currency:', portfolioSummary.currency || currency);
    
    return formatCurrency(balance);
  };

  const getLockDurationText = (days) => {
    if (days >= 1825) return '5+ years';
    if (days >= 365) return '1-5 years';
    if (days >= 364) return '52 weeks';
    if (days >= 224) return '32 weeks';
    if (days >= 112) return '16 weeks';
    return `${days} days`;
  };

  const getLockEndDate = () => {
    const minutes = questionnaire.lock_duration_days;
    const endDate = new Date();
    endDate.setMinutes(endDate.getMinutes() + minutes);
    return endDate.toLocaleString('en-US', { 
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRiskProfileColor = (profile) => {
    switch (profile) {
      case 'Conservative': return '#3b82f6';
      case 'Moderate': return '#f59e0b';
      case 'Aggressive': return '#dc2626';
      default: return '#666';
    }
  };

  const getWithdrawalPreferenceText = () => {
    switch (questionnaire.withdrawal_preference) {
      case 'PROFIT_PERIODIC':
        return `Withdraw ${questionnaire.profit_withdrawal_percentage}% of profits every 1 minute (TEST: normally 4 weeks)`;
      case 'FIXED_PERIODIC':
        const amountInNGN = parseFloat(questionnaire.fixed_withdrawal_amount);
        let withdrawalDisplay;
        if (currency === 'USD' && exchangeRate) {
          const amountInUSD = convertFromNGN(amountInNGN);
          withdrawalDisplay = formatCurrency(amountInUSD);
        } else {
          withdrawalDisplay = formatCurrency(amountInNGN);
        }
        return `Withdraw ${withdrawalDisplay} every 1 minute (TEST: normally 4 weeks)`;
      case 'NO_WITHDRAWAL':
        return 'No withdrawal until maturity';
      default:
        return 'Not specified';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!questionnaire.intended_investment_amount) {
      setError('Investment amount is required');
      return;
    }

    setLoading(true);

    try {
      const investmentData = {
        stock_id: stock.id,
        questionnaire_id: questionnaire.id,
        use_intended_amount: true,
        input_currency: currency
      };

      console.log('📤 Creating investment:', investmentData);
      const response = await investmentService.createInvestment(investmentData);
      onSuccess(response.investment);
    } catch (err) {
      setError(err.message || 'Failed to create investment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="investment-modal-overlay" onClick={onClose}>
      <div className="investment-creation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="investment-modal-header">
          <div>
            <h2>Confirm Investment</h2>
            <p className="stock-name">{stock.symbol} - {stock.name}</p>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="questionnaire-results">
          <h3>Your Investment Profile</h3>
          <div className="profile-cards">
            <div className="profile-card">
              <div className="profile-label">Risk Profile</div>
              <div 
                className="profile-value risk-profile" 
                style={{ color: getRiskProfileColor(questionnaire.risk_profile) }}
              >
                {questionnaire.risk_profile}
              </div>
            </div>
            <div className="profile-card">
              <div className="profile-label">Lock Duration</div>
              <div className="profile-value">
                {getLockDurationText(questionnaire.lock_duration_days)}
              </div>
            </div>
          </div>

          <div className="withdrawal-settings-display">
            <h4>Withdrawal Plan</h4>
            <div className="withdrawal-info">
              <span className="info-icon">💰</span>
              <span className="withdrawal-text">{getWithdrawalPreferenceText()}</span>
            </div>
          </div>

          <div className="lock-warning">
            <span className="warning-icon">🔒</span>
            <span>Your investment will be locked until <strong>{getLockEndDate()}</strong></span>
          </div>
        </div>

        <div className="wallet-display">
          <span className="wallet-label">Available Balance:</span>
          <span className="wallet-amount">
            {portfolioSummary ? getWalletBalanceDisplay() : 'Loading...'}
          </span>
        </div>

        <div className="investment-summary-section">
          <h3>Investment Summary</h3>
          
          <div className="summary-highlight">
            <div className="highlight-item total-investment">
              <span className="highlight-label">Total Investment:</span>
              <span className="highlight-value">{getInvestmentAmountDisplay()}</span>
            </div>
          </div>

          <div className="investment-summary">
            <div className="summary-item">
              <span>Stock:</span>
              <span>{stock.symbol} - {stock.name}</span>
            </div>
            <div className="summary-item">
              <span>Entry Price:</span>
              <span>{getStockPriceDisplay()}</span>
            </div>
            <div className="summary-item">
              <span>Quantity:</span>
              <span>{calculateQuantity().toFixed(6)} shares</span>
            </div>
            <div className="summary-item">
              <span>Lock Period:</span>
              <span>{getLockDurationText(questionnaire.lock_duration_days)}</span>
            </div>
            <div className="summary-item">
              <span>Maturity Date:</span>
              <span>{getLockEndDate()}</span>
            </div>
            <div className="summary-item withdrawal-summary">
              <span>Withdrawal Plan:</span>
              <span className="withdrawal-plan-text">
                {questionnaire.withdrawal_preference === 'PROFIT_PERIODIC' && (
                  `${questionnaire.profit_withdrawal_percentage}% profit every 4 weeks`
                )}
                {questionnaire.withdrawal_preference === 'FIXED_PERIODIC' && (() => {
                  const amountInNGN = parseFloat(questionnaire.fixed_withdrawal_amount);
                  let display;
                  if (currency === 'USD' && exchangeRate) {
                    display = formatCurrency(convertFromNGN(amountInNGN));
                  } else {
                    display = formatCurrency(amountInNGN);
                  }
                  return `${display} every 4 weeks`;
                })()}
                {questionnaire.withdrawal_preference === 'NO_WITHDRAWAL' && 'At maturity only'}
              </span>
            </div>
          </div>
        </div>

        {currency === 'USD' && exchangeRate && (
          <div style={{padding: '10px', background: '#f0f0f0', borderRadius: '4px', margin: '10px 0', fontSize: '12px'}}>
            💱 Exchange Rate: 1 USD = ₦{parseFloat(exchangeRate.usd_to_ngn).toFixed(2)} | 1 NGN = ${parseFloat(exchangeRate.ngn_to_usd).toFixed(6)}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="action-buttons">
          <button type="button" className="cancel-button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="button" className="confirm-button" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating Investment...' : 'Confirm Investment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestmentCreation;